import { useLocalStorage } from '@vueuse/core'
import { createRequestScopedComposable } from '../utils/createRequestScopedComposable'

export type ColorThemeOption = 'system' | 'light' | 'dark'

export function isColorTheme(value: string): value is ColorThemeOption {
  return ['system', 'light', 'dark'].includes(value)
}

function toColorModePreference(theme: string | undefined): ColorThemeOption {
  if (theme === 'auto') return 'system'
  if (theme === 'light' || theme === 'dark') return theme
  return 'system'
}

export const useTheme = createRequestScopedComposable(() => {
  const siteSettingsStore = useSiteSettingsStore()
  const { data: siteSettings } = storeToRefs(siteSettingsStore)
  const colorMode = useColorMode()
  const isUserSelected = useLocalStorage('app:userSelectedTheme', false)
  if (!isUserSelected.value) {
    colorMode.preference = toColorModePreference(
      siteSettings.value.userSettings?.defaultSettings.theme
    )
  }

  const mixpanelPropertyName = computed(() => {
    if (colorMode.preference === 'system' && colorMode.value === 'light')
      return 'auto-light'
    else if (colorMode.preference === 'system' && colorMode.value === 'dark')
      return 'auto-dark'
    return colorMode.preference
  })

  const updateTheme = (newTheme: ColorThemeOption) => {
    const { track } = useAnalytics()

    isUserSelected.value = true
    colorMode.preference = newTheme
    document.documentElement.className = `${colorMode.value} bg-background-default dark:bg-background-neutral-subtle`
    track('NtpSetting_Changed', {
      setting_name: 'ntp_theme',
      setting_value: mixpanelPropertyName.value,
    })
    setProfileProperty('ntp_theme', mixpanelPropertyName.value)
  }

  colorMode.preference = isUserSelected.value
    ? colorMode.preference
    : toColorModePreference(
        siteSettings.value.userSettings?.defaultSettings.theme
      )

  return { theme: colorMode, updateTheme, mixpanelPropertyName }
})
